import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { Button } from './Button';
import { useStore } from '../../store/StoreContext';
import { useAuth, useCan } from '../../auth/AuthContext';

// AssigneePicker — sets/clears estimates.assigned_to (UUID[]).
//
// Multi-select per May 2026 sales-lead workflow: a single estimate can
// be assigned to multiple office users (e.g. Lisa primary + Taylor for
// next-day coverage). Anyone in the array has SELECT + status-change
// access via RLS; editing pricing is still gated by the edit_pricing
// capability (default admin only). So multi-assign expands DELIVERY
// power, not BUILD power.
//
// Editable mode (admin+ via the assign_estimates capability):
//   • Trigger opens a portal-anchored dropdown with one checkbox per
//     user_profile. Click a name to toggle their membership in the array.
//   • Persists immediately via saveEstimate after each toggle.
//
// Display-only mode (everyone else):
//   • Static badge showing the assignee list (or "Unassigned").
//   • Office staff usually only see assignees on rows they themselves
//     are in (RLS), so this is mostly a confirmation surface.
//
// variants:
//   'estimates-row' — compact full-height block used as a column on the
//                     Estimates page row (matches Priority/Status width).
//   'inline'        — small badge for embedding inline next to other meta.
//   'modal'         — full-width field for use inside EstimateModal.
export default function AssigneePicker({ estimate, variant = 'inline', onChanged }) {
    const can = useCan();
    const { user } = useAuth();
    const { userProfiles, saveEstimate } = useStore();
    const canAssign = can('assign_estimates');

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const triggerRef = useRef(null);
    const [menuPos, setMenuPos] = useState(null);

    // Normalize the current value — Postgres returns UUID[] as a JS array,
    // but legacy rows / first-save shapes might be null/undefined. Always
    // work with an array so the toggle math is simple.
    const assignedIds = Array.isArray(estimate.assigned_to) ? estimate.assigned_to : [];

    // Resolve to display names. Maintain the order the IDs are stored in
    // so the UI is deterministic across renders.
    const assignedProfiles = assignedIds
        .map(id => userProfiles.find(p => p.user_id === id))
        .filter(Boolean);
    const assignedNames = assignedProfiles.map(
        p => p.display_name || p.email?.split('@')[0] || 'Unknown'
    );

    // Display string — fits in the chip without truncating in most cases:
    //   []                       → "Unassigned"
    //   ["Lisa"]                 → "Lisa"
    //   ["Lisa","Taylor"]        → "Lisa, Taylor"
    //   ["Lisa","Taylor","Sam"]  → "Lisa +2"
    let label;
    if (assignedNames.length === 0)        label = 'Unassigned';
    else if (assignedNames.length === 1)   label = assignedNames[0];
    else if (assignedNames.length === 2)   label = `${assignedNames[0]}, ${assignedNames[1]}`;
    else                                    label = `${assignedNames[0]} +${assignedNames.length - 1}`;

    // Tooltip — always shows the full list so hovering a "+N" chip reveals
    // who all is on the estimate.
    const tooltip = assignedNames.length > 0 ? assignedNames.join(', ') : 'Unassigned';

    // True iff the current viewer is in the assignee list. Highlights the
    // chip green so an office user can find "their" estimates at a glance.
    const includesMe = !!(user?.id && assignedIds.includes(user.id));

    // Value color for the estimates-row block (mirrors Priority's "colored
    // label on a slate body" treatment): slate when unassigned, emerald when
    // it's me, blue when assigned to someone else.
    const valueColor = assignedIds.length === 0 ? '#64748b' : includesMe ? '#047857' : '#1d4ed8';

    // Shared estimates-row block markup (header band + slate body) so the
    // Assigned column matches the Status + Priority blocks pixel-for-pixel.
    // Returns a FRAGMENT — the caller's <button>/<div> is the
    // `w-full h-full flex flex-col` container (same as PriorityPicker), so
    // the h-full chain stays intact and the value centers identically.
    const renderRowBlock = (withChevron) => (
        <>
            <div className="flex-shrink-0 pt-1.5 pb-1 px-2 flex items-center justify-center bg-white">
                <span className="font-lemonmilk text-[10px] uppercase leading-none whitespace-nowrap text-slate-900" style={{ letterSpacing: '0.08em' }}>Assigned</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center bg-slate-300 px-1.5 py-1 min-w-0 gap-0.5">
                <span data-testid="assignee-label" className="font-lemonmilk uppercase leading-none whitespace-nowrap truncate text-center text-sm w-full" style={{ color: valueColor }}>
                    {label}
                </span>
                {withChevron && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={valueColor} strokeWidth="3" className="flex-shrink-0">
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                )}
            </div>
        </>
    );

    // Position the menu directly below the trigger. Recomputed each open
    // so scroll position doesn't break alignment.
    useEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuPos({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: Math.max(rect.width, 220),
        });
    }, [open]);

    // Close on outside click / Esc.
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (triggerRef.current?.contains(e.target)) return;
            // Menu is in a portal — check via the marker data-attr.
            if (e.target.closest?.('[data-assignee-menu]')) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // Toggle a single user's membership in the array. We DON'T close the
    // menu on each click — multi-select stays open so the admin can tick
    // multiple boxes in one motion. Click outside / Esc / Done closes.
    const toggleAssignee = async (userId) => {
        const next = assignedIds.includes(userId)
            ? assignedIds.filter(id => id !== userId)
            : [...assignedIds, userId];
        await persist(next);
    };

    const clearAll = async () => {
        if (assignedIds.length === 0) return;
        await persist([]);
    };

    const persist = async (nextList) => {
        setSaving(true);
        try {
            // Persist as null when empty so the column reads cleanly as
            // "unassigned" rather than "explicitly assigned to no one."
            const value = nextList.length > 0 ? nextList : null;
            await saveEstimate({ ...estimate, assigned_to: value });
            onChanged?.(value);
        } catch (e) {
            console.error('Failed to update assignees:', e);
        } finally {
            setSaving(false);
        }
    };

    // ── Display-only render path ────────────────────────────────────────
    if (!canAssign) {
        if (variant === 'estimates-row') {
            return <div className="w-full h-full flex flex-col overflow-hidden" title={tooltip}>{renderRowBlock(false)}</div>;
        }
        return (
            <span
                title={tooltip}
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${includesMe ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                {label}
            </span>
        );
    }

    // ── Editable trigger ────────────────────────────────────────────────
    const triggerColor = assignedIds.length === 0
        ? 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-400 hover:bg-slate-100'
        : includesMe
            ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:border-emerald-500'
            : 'bg-blue-50 text-blue-700 border-blue-300 hover:border-blue-500';

    const sortedProfiles = [...userProfiles].sort((a, b) => {
        const an = (a.display_name || a.email || '').toLowerCase();
        const bn = (b.display_name || b.email || '').toLowerCase();
        return an.localeCompare(bn);
    });

    const menu = open && menuPos && createPortal(
        <div
            data-assignee-menu
            style={{ position: 'absolute', top: menuPos.top, left: menuPos.left, minWidth: menuPos.width, zIndex: 9000 }}
            className="bg-white border border-slate-200 rounded-lg shadow-xl py-1 max-h-80 overflow-auto">
            {/* Header row — count + Clear-all */}
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-600">
                    {assignedIds.length === 0
                        ? 'No one assigned'
                        : `${assignedIds.length} assigned`}
                </span>
                {assignedIds.length > 0 && (
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={clearAll}>
                        Clear all
                    </Button>
                )}
            </div>
            {sortedProfiles.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400 italic">No users found</div>
            )}
            {sortedProfiles.map(p => {
                const label = p.display_name || p.email || '(no name)';
                const checked = assignedIds.includes(p.user_id);
                return (
                    <button
                        key={p.user_id}
                        type="button"
                        data-testid="assignee-option"
                        data-user-id={p.user_id}
                        onClick={() => toggleAssignee(p.user_id)}
                        disabled={saving}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50 ${checked ? 'bg-blue-50/40' : ''}`}>
                        {/* Custom checkbox to dodge the Tailwind-CDN native-input
                            quirk (see CLAUDE.md). Same pattern as PermCheckbox. */}
                        <span
                            className={`inline-flex items-center justify-center w-4 h-4 rounded border-2 flex-shrink-0 transition-colors ${checked ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}
                            aria-hidden="true">
                            {checked && (
                                <svg viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.5" className="w-3 h-3">
                                    <path d="M3 8l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </span>
                        <span className={`flex-1 truncate ${checked ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{label}</span>
                        {p.role && (
                            <span className="text-[9px] font-semibold uppercase text-slate-400 flex-shrink-0">{p.role}</span>
                        )}
                    </button>
                );
            })}
            <div className="border-t border-slate-100 mt-1 pt-1 px-3 py-1.5 flex justify-end">
                <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setOpen(false)}>
                    Done
                </Button>
            </div>
        </div>,
        portalRoot()
    );

    if (variant === 'estimates-row') {
        return (
            <>
                <button
                    type="button"
                    ref={triggerRef}
                    disabled={saving}
                    title={tooltip}
                    data-testid="assignee-trigger"
                    onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                    className="w-full h-full flex flex-col overflow-hidden text-left cursor-pointer hover:brightness-95 focus:outline-none disabled:opacity-50">
                    {renderRowBlock(true)}
                </button>
                {menu}
            </>
        );
    }

    if (variant === 'modal') {
        return (
            <>
                <button
                    type="button"
                    ref={triggerRef}
                    disabled={saving}
                    title={tooltip}
                    onClick={() => setOpen(o => !o)}
                    className={`w-full px-3 py-2 text-sm font-semibold rounded-lg border-2 transition-all flex items-center justify-between gap-2 disabled:opacity-50 ${triggerColor}`}>
                    <span className="truncate">{label}</span>
                    <span className="text-xs opacity-60">▾</span>
                </button>
                {menu}
            </>
        );
    }

    // 'inline' default — small chip
    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                disabled={saving}
                title={tooltip}
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-colors disabled:opacity-50 ${triggerColor}`}>
                <span>{label}</span>
                <span className="opacity-50">▾</span>
            </button>
            {menu}
        </>
    );
}
